/**
 * Horizontal swipe detection for advancing onboarding screens.
 *
 * Accepts swipes in either direction because the onboarding flow is
 * strictly forward — there's no "back" to confuse with a swipe right.
 * Either direction just means "advance" to whichever gesture the user
 * intuitively reaches for. Threshold is ~60 px to avoid false fires
 * on small drags.
 *
 * Returns bind handlers; spread them onto the element that should be
 * swipeable. The onSwipe callback only fires once per gesture.
 */
import { useCallback, useRef } from "react";

interface SwipeBind {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

export function useSwipe(onSwipe: () => void, threshold = 60): SwipeBind {
  const startXRef = useRef<number | null>(null);
  const startYRef = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0]?.clientX ?? null;
    startYRef.current = e.touches[0]?.clientY ?? null;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (startXRef.current === null || startYRef.current === null) return;
      const endX = e.changedTouches[0]?.clientX ?? startXRef.current;
      const endY = e.changedTouches[0]?.clientY ?? startYRef.current;
      const dx = endX - startXRef.current;
      const dy = endY - startYRef.current;
      startXRef.current = null;
      startYRef.current = null;
      // Require horizontal dominance so vertical scroll doesn't fire.
      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
        onSwipe();
      }
    },
    [onSwipe, threshold]
  );

  return { onTouchStart, onTouchEnd };
}
